<template>
  <div style="width: 100%; height: calc(100% - 70px); margin: 0 !important">
    <sh-page theme="light">
      <sh-nav-bar slot="nav">
        <sh-dropdown
          style="width: auto"
          slot="functions"
          label="Division"
          :value="selectedDivision.label"
          ref="imagingSelector"
        >
          <sh-menu-item
            v-for="(item, index) in division"
            :key="index"
            :label="item.label"
            :active="item.value == selectedDivision.value"
            @click.prevent="callSetSelectedDivision"
          >
          </sh-menu-item>
        </sh-dropdown>

        <sh-button
          slot="functions"
          label="Back to Country Selection"
          icon="reset"
          color="secondary"
          tooltip="left"
          @click="goToCountriesPage"
        ></sh-button>
      </sh-nav-bar>

      <div id="mapContainer">
        <div id="map"></div>
        <sh-spinner
          style="position: absolute"
          overlay="true"
          v-if="loadingMapData"
          label="Loading Areas Map"
        ></sh-spinner>
      </div>
    </sh-page>
  </div>
</template>

<script>
import mapboxgl from "mapbox-gl";

import { mapActions, mapGetters, mapMutations } from "vuex";
import router from "../router";

export default {
  name: "CountyMap",
  props: {
    country: { type: String, required: true, default: "US" },
  },
  data() {
    return {
      map: null,
      loadingMapData: false,
    };
  },
  computed: {
    ...mapGetters([
      "accessToken",
      "selectedDivision",
      "areas",
      "division",
      "tilesets",
      "selectedCountry",
      "countries",
    ]),
    ...mapGetters("oidcStore", [
      "oidcIsAuthenticated",
      "oidcAuthenticationIsChecked",
      "oidcUser",
      "oidcIdToken",
      "oidcIdTokenExp",
    ]),
  },
  async mounted() {
    // console.log(
    //   "___________________________CountyMap Mounted___________________________"
    // );
    // console.log(this.oidcIsAuthenticated);
    // console.log("oidcIdToken ", this.oidcIdToken);
    // console.log(this.$store.state.oidcStore);

    if (JSON.stringify(this.selectedCountry) === "{}") {
      await this.getCountriesData();
    }
    this.getAreasData();
  },
  destroyed() {
    console.log("CountyMap destroyed!");
  },
  beforeUnmount() {
    if (this.map) {
      this.map.remove();
    }
  },
  methods: {
    ...mapActions(["loadAreas", "loadCountries"]),
    ...mapMutations([
      "setSelectedArea",
      "setSelectedDivision",
      "setSelectedCountry",
    ]),

    callSetSelectedDivision($event) {
      if ($event.target.label) {
        if ($event.target.label !== "Division") {
          let indexInDivisions = this.division.findIndex((div) => {
            return div.label === $event.target.label;
          });

          if (indexInDivisions !== -1) {
            this.setSelectedDivision(this.division[indexInDivisions]);
          }
        }
      }
    },

    async getAreasData() {
      this.loadingMapData = true;
      await this.loadAreas();

      this.initMap();

      this.map.once("idle", () => {
        this.map.resize();
        this.loadingMapData = false;
      });
    },

    async getCountriesData() {
      await this.loadCountries();

      let index = this.countries.findIndex((el) => {
        return el.iso_3166_1 === this.country;
      });

      if (index !== -1) {
        this.setSelectedCountry(this.countries[index]);
      }
    },

    initMap() {
      mapboxgl.accessToken = this.accessToken;

      this.map = new mapboxgl.Map({
        container: "map",
        style: "mapbox://styles/mapbox/light-v10",
        center: this.tilesets[this.selectedCountry.iso_3166_1]["mapCenter"],
        minZoom: this.tilesets[this.selectedCountry.iso_3166_1]["minZoom"],
        maxZoom: this.tilesets[this.selectedCountry.iso_3166_1]["maxZoom"],
        zoom: this.tilesets[this.selectedCountry.iso_3166_1]["zoom"],
      });

      this.map.addControl(new mapboxgl.ScaleControl());
      this.map.on("load", () => {
        this.map.getCanvas().style.cursor = "default";
        // https://docs.mapbox.com/mapbox-gl-js/example/geojson-layer-in-stack/
        const layers = this.map.getStyle().layers;
        // Find the index of the first symbol layer in the map style.
        let firstSymbolId;
        for (const layer of layers) {
          if (layer.type === "symbol") {
            firstSymbolId = layer.id;
            break;
          }
        }

        console.log(firstSymbolId, router);

        this.map.addSource("counties", {
          type: "vector",
          url: this.tilesets[this.selectedCountry.iso_3166_1]["mapboxUrl"],
        });

        this.map.addLayer(
          {
            id: "counties",
            type: "fill",
            source: "counties",
            "source-layer":
              this.tilesets[this.selectedCountry.iso_3166_1]["sourceLayer"], //"cb_2021_us_county_500k-cuyvi1",
            paint: {
              "fill-outline-color": "transparent", //"rgba(0,0,0,0.1)",
              "fill-color": "rgba(0,0,0,0.1)",
            },
          },
          "settlement-label"
        ); // Place polygon under these labels.

        if (this.selectedCountry.iso_3166_1 == "US") {
          // this is better as it alows a county to belong to multiple areas
          this.areas.forEach((area) => {
            this.map.addLayer(
              {
                id: area.name,
                type: "fill",
                source: "counties",
                "source-layer":
                  this.tilesets[this.selectedCountry.iso_3166_1]["sourceLayer"],
                paint: {
                  "fill-outline-color": "transparent",
                  "fill-color": [
                    "match",
                    [
                      "get",
                      this.tilesets[this.selectedCountry.iso_3166_1][
                        "countyProp"
                      ],
                    ],
                    area.counties,
                    area.color,
                    "transparent",
                  ],
                  "fill-opacity": 1,
                },
                filter: [
                  "in",
                  this.tilesets[this.selectedCountry.iso_3166_1]["countyProp"],
                  ...area.counties,
                ],
              },
              firstSymbolId
            );
            if (area.division === this.selectedDivision.value) {
              this.map.setLayoutProperty(area.name, "visibility", "visible");
            } else this.map.setLayoutProperty(area.name, "visibility", "none");
          });
          this.map.addLayer(
            {
              id: "counties-highlighted",
              type: "fill",
              source: "counties",
              "source-layer":
                this.tilesets[this.selectedCountry.iso_3166_1]["sourceLayer"],
              paint: {
                // "fill-outline-color": "#042024",
                "fill-color": "#2f5c62",
                "fill-opacity": 1,
              },
              filter: [
                "in",
                this.tilesets[this.selectedCountry.iso_3166_1]["countyProp"],
                "",
              ],
            },
            firstSymbolId
          );
          const areaName_popup = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
          });
          this.areas.forEach((area) => {
            this.map.on("mousemove", area.name, (e) => {
              this.map.getCanvas().style.cursor = "pointer";

              this.map.setFilter("counties-highlighted", [
                "in",
                this.tilesets[this.selectedCountry.iso_3166_1]["countyProp"],
                ...area.counties,
              ]);

              areaName_popup
                .setLngLat(e.lngLat)
                .setHTML(area.name)
                .addTo(this.map);
            });
            this.map.on("mouseleave", area.name, () => {
              this.map.getCanvas().style.cursor = "default";
              this.map.setFilter("counties-highlighted", [
                "in",
                this.tilesets[this.selectedCountry.iso_3166_1]["countyProp"],
                "",
              ]);
              areaName_popup.remove();
            });
            this.map.on("click", area.name, () => {
              console.log(area.name);

              this.setSelectedArea(area);

              router.push({
                name: "CockpitPage",
                params: {
                  area: area.name,
                  selecteddivision: this.selectedDivision.value,
                  country: this.selectedCountry.iso_3166_1,
                },
                // props: { },
              });
            });
          });
        } else if (this.selectedCountry.iso_3166_1 == "CA") {
          this.areas.forEach((area) => {
            let filterCondition = ["any"];
            area.counties.forEach((county) => {
              filterCondition.push([
                "in",
                county,
                [
                  "get",
                  this.tilesets[this.selectedCountry.iso_3166_1]["countyProp"],
                ],
              ]);
            });

            this.map.addLayer(
              {
                id: area.name,
                type: "fill",
                source: "counties",
                "source-layer":
                  this.tilesets[this.selectedCountry.iso_3166_1]["sourceLayer"],
                paint: {
                  "fill-outline-color": "transparent",
                  "fill-color": [
                    "case",
                    filterCondition,
                    area.color,
                    "transparent",
                  ],
                  "fill-opacity": 1,
                },
                filter: filterCondition,
              },
              "settlement-label"
            );
            if (area.division === this.selectedDivision.value) {
              this.map.setLayoutProperty(area.name, "visibility", "visible");
            } else this.map.setLayoutProperty(area.name, "visibility", "none");
          });

          this.map.addLayer(
            {
              id: "counties-highlighted",
              type: "fill",
              source: "counties",
              "source-layer":
                this.tilesets[this.selectedCountry.iso_3166_1]["sourceLayer"],
              paint: {
                "fill-color": "#2f5c62",
                "fill-opacity": 1,
              },
              filter: [
                "in",
                this.tilesets[this.selectedCountry.iso_3166_1]["countyProp"],
                "",
              ],
            },
            firstSymbolId
          );
          this.map.moveLayer("counties-highlighted", null);
          const areaName_popup = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
          });
          this.areas.forEach((area) => {
            let filterCondition = ["any"];
            area.counties.forEach((county) => {
              filterCondition.push([
                "in",
                county,
                [
                  "get",
                  this.tilesets[this.selectedCountry.iso_3166_1]["countyProp"],
                ],
              ]);
            });
            this.map.on("mousemove", area.name, (e) => {
              this.map.getCanvas().style.cursor = "pointer";

              this.map.setFilter("counties-highlighted", filterCondition);

              areaName_popup
                .setLngLat(e.lngLat)
                .setHTML(area.name)
                .addTo(this.map);
            });
            this.map.on("mouseleave", area.name, () => {
              this.map.getCanvas().style.cursor = "default";
              this.map.setFilter("counties-highlighted", [
                "in",
                this.tilesets[this.selectedCountry.iso_3166_1]["countyProp"],
                "",
              ]);
              areaName_popup.remove();
            });
            this.map.on("click", area.name, () => {
              console.log(area.name);

              this.setSelectedArea(area);

              router.push({
                name: "CockpitPage",
                params: {
                  area: area.name,
                  selecteddivision: this.selectedDivision.value,
                  country: this.selectedCountry.iso_3166_1,
                },
                // props: { },
              });
            });
          });
        }
      });
    },

    goToCountriesPage() {
      this.setSelectedCountry({});
      this.$router.push({ name: "GlobalMap" });
    },
  },
  watch: {
    selectedDivision: function (val) {
      if (val) {
        this.areas.forEach((area) => {
          if (area.division === this.selectedDivision.value) {
            this.map.setLayoutProperty(area.name, "visibility", "visible");
          } else this.map.setLayoutProperty(area.name, "visibility", "none");
        });
      }
    },
  },
};
</script>

<style scoped lang="scss">
#map {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 100%;
  border-radius: 0.25rem;
  border: 1px solid #ccc;
}

.mapboxgl-map {
  font-family: "siemens sans", sans-serif;
}

.mapboxgl-popup-content {
  text-align: center;
  font-family: "siemens sans", sans-serif;
}
</style>
